import React, { useEffect, useState } from "react";
import {
    AdminPanelSettings as AdminIcon,
    AutoFixHigh as AutoFixHighIcon,
    Home as HomeIcon,
    Login as LoginIcon,
    Logout as LogoutIcon,
    Menu as MenuIcon
} from "@mui/icons-material";
import Logger from "../../../services/Logger";
import {
    AppBar,
    Box,
    Button,
    Chip,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import AuthService, { AUTH_CHANGED_EVENT } from "../../../services/AuthService";
import GeoService from "../../../services/GeoService";
import { detectBrowserInterfaceLanguage, getInterfaceLanguage, hasInterfaceLanguage, INTERFACE_LANG_KEY, setInterfaceLanguage } from "../../../services/LangPrefs";
import UserService from "../../../services/UserService";
import { usePagesTranslation } from "../../locales";
import { ACCENT, SURFACE, TEXT } from "../../../dark-theme/palette";

const HEADER_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/logo32.png`;

/** Light chrome for the layouts outside the site theme (creator, ad captures). */
const LIGHT_TONE = {
    backgroundColor: "#fff",
    color: "#333",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
};

/** Site chrome from the theme tokens — see `src/dark-theme`. */
const SITE_TONE = {
    backgroundColor: SURFACE.chrome,
    color: TEXT.primary,
    boxShadow: "0 2px 10px rgba(2, 6, 18, 0.45)",
};

/**
 * Site header: logo, title, menu, auth — no date under the title.
 * `tone="dark"` puts it on the site theme; creator and ad layouts stay light.
 */
const MinimalHeader = ({
    title = "Puzzle Interact",
    onMenuClick,
    centeredTitle = null,
    centeredSubtitle = null,
    tone = "light",
    headerExtra = null,
}) => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const headerTone = tone === "dark" ? SITE_TONE : LIGHT_TONE;
    /** Brand green needs to lift on the dark chrome to stay legible. */
    const titleColor = tone === "dark" ? ACCENT.light : "#3BA666";
    const signInColor = tone === "dark" ? ACCENT.light : "#2e7d32";
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const appBarSafeAreaPt = isMobile
        ? "max(env(safe-area-inset-top, 0px), 12px)"
        : "env(safe-area-inset-top, 0px)";

    const [isAdmin, setIsAdmin] = useState(false);
    const [isCreator, setIsCreator] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [language, setLanguage] = useState(() => {
        // Load UI language from localStorage if available
        return getInterfaceLanguage();
    });
    const [hasDetectedRegion, setHasDetectedRegion] = useState(false);
    const [isQuebec, setIsQuebec] = useState(false);

    const onAdminClick = () => {
        navigate('/admin');
    };

    const onCreatorClick = () => {
        navigate('/creator');
    };

    const onHomeClick = () => {
        navigate('/');
    };

    // Check if we're on creator pages
    const isOnCreatorPage = location.pathname.startsWith('/creator');

    const onSignInClick = () => {
        navigate("/login", { state: { from: { pathname: location.pathname } } });
    };

    const onSignOutClick = async () => {
        navigate('/');
        try {
            await AuthService.logout();
        } catch (error) {
            Logger.error("Error during sign out:", error);
        }
    };

    // Detect region from IP to set default language (only once if no stored preference)
    useEffect(() => {
        const detectRegionAndSetLanguage = async () => {
            // Only detect if we haven't detected yet
            if (hasDetectedRegion) return;
            
            // Always check Quebec status for language order display
            let quebecStatus = false;
            try {
                quebecStatus = await GeoService.isQuebec();
                setIsQuebec(quebecStatus);
            } catch (error) {
                Logger.error("Error detecting region:", error);
                setIsQuebec(false);
            }
            
            // Priority 1: Stored UI language (includes first-run default seeded in index.js from the browser)
            if (hasInterfaceLanguage()) {
                const langToUse = getInterfaceLanguage();
                setInterfaceLanguage(langToUse);
                setLanguage(langToUse);
                setHasDetectedRegion(true);
                return;
            }

            // Priority 2: No stored preference (e.g. tests / edge) — match browser if supported, else English
            const langToUse = detectBrowserInterfaceLanguage();
            setInterfaceLanguage(langToUse);
            setLanguage(langToUse);

            setHasDetectedRegion(true);
        };

        detectRegionAndSetLanguage();
    }, [hasDetectedRegion]);

    useEffect(() => {
        const refreshHeaderAuth = () => {
            AuthService.isAuthenticated().then((authenticated) => {
                setIsAuthenticated(authenticated);
            });
            UserService.getUserInfo()
                .then((u) => {
                    setIsAdmin(u?.cloudUser?.userRole === "ADMIN");
                    setIsCreator(
                        u?.cloudUser?.userRole === "CREATOR" || u?.cloudUser?.userRole === "MANAGER"
                    );
                })
                .catch(() => {
                    setIsAdmin(false);
                    setIsCreator(false);
                });
        };

        refreshHeaderAuth();
        window.addEventListener(AUTH_CHANGED_EVENT, refreshHeaderAuth);
        return () => window.removeEventListener(AUTH_CHANGED_EVENT, refreshHeaderAuth);
    }, []);

    // Listen for localStorage changes to update UI language
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === INTERFACE_LANG_KEY) {
                const newUiLanguage = getInterfaceLanguage();
                setLanguage(newUiLanguage);
            }
        };

        // Listen for storage events (changes from other tabs/windows)
        window.addEventListener('storage', handleStorageChange);

        // Also check for changes within the same window
        const checkLocalStorageChanges = () => {
            const currentUiLang = getInterfaceLanguage();
            if (language !== currentUiLang) {
                setLanguage(currentUiLang);
            }
        };

        // Check for changes periodically (every 1 second)
        const intervalId = setInterval(checkLocalStorageChanges, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(intervalId);
        };
    }, [language]);

    const signInLabel = t("login.header-sign-in", "Sign In");
    const dashboardLabel = t("nav.dashboard", "Dashboard");

    const renderDashboardChip = () =>
        isAuthenticated ? (
            <Chip
                component={RouterLink}
                to="/dashboard"
                clickable
                label={dashboardLabel}
                sx={{
                    height: 36,
                    minHeight: 44,
                    px: 0.5,
                    fontWeight: 700,
                    flexShrink: 0,
                    bgcolor: ACCENT.soft,
                    color: tone === "dark" ? ACCENT.light : ACCENT.dark,
                    border: `1px solid ${ACCENT.main}`,
                    "& .MuiChip-label": { px: 1.25 },
                    "&:hover": {
                        bgcolor: ACCENT.main,
                        color: ACCENT.contrastText,
                    },
                }}
            />
        ) : null;

    const renderSignInControl = (sx = {}) =>
        !isAuthenticated ? (
            <Button
                color="inherit"
                onClick={onSignInClick}
                startIcon={<LoginIcon sx={{ fontSize: 20 }} />}
                sx={{
                    minHeight: 44,
                    px: { xs: 1.25, sm: 1.5 },
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    textTransform: "none",
                    color: signInColor,
                    flexShrink: 0,
                    touchAction: "manipulation",
                    ...sx,
                }}
            >
                {signInLabel}
            </Button>
        ) : (
            <Tooltip title="Sign out">
                <IconButton
                    color="inherit"
                    onClick={onSignOutClick}
                    sx={{ minWidth: 44, minHeight: 44, touchAction: "manipulation", ...sx }}
                >
                    <LogoutIcon />
                </IconButton>
            </Tooltip>
        );

    return (
        <>
            {centeredTitle ? (
                // Centered title header
                <AppBar
                    position="static"
                    data-site-chrome="header"
                    sx={{
                        ...headerTone,
                        minHeight: isMobile ? 56 : 64,
                        zIndex: 1100,
                        position: 'relative',
                        pt: appBarSafeAreaPt,
                    }}
                >
                    <Toolbar sx={{
                        minHeight: isMobile ? '56px !important' : '64px !important',
                        position: 'relative',
                        flexDirection: 'row',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        py: isMobile ? 1 : 0,
                        px: isMobile ? 2 : 3
                    }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={onMenuClick}
                            sx={{
                                mr: 2,
                                zIndex: 1001,
                                position: 'relative',
                                alignSelf: isMobile ? 'flex-start' : 'center',
                                flexShrink: 0,
                                minWidth: 44,
                                minHeight: 44,
                                touchAction: 'manipulation'
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Centered Title */}
                        <Box sx={{ 
                            position: isMobile ? 'static' : 'absolute',
                            left: isMobile ? 'auto' : '64px',
                            right: isMobile ? 'auto' : '160px',
                            top: isMobile ? 'auto' : '50%',
                            transform: isMobile ? 'none' : 'translateY(-50%)',
                            display: 'flex',
                            justifyContent: isMobile ? 'flex-start' : 'center',
                            alignItems: isMobile ? 'flex-start' : 'center',
                            pointerEvents: 'none',
                            px: isMobile ? 0 : 1,
                            py: isMobile ? 0 : 0,
                            flex: isMobile ? 1 : 'none',
                            minWidth: 0,
                            zIndex: 1
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: isMobile ? 'flex-start' : 'center',
                                    gap: 1,
                                    width: '100%',
                                    maxWidth: '100%'
                                }}
                            >
                                <Box
                                    component={RouterLink}
                                    to="/"
                                    aria-label="Home"
                                    sx={{
                                        display: 'inline-flex',
                                        flexShrink: 0,
                                        pointerEvents: 'auto',
                                        textDecoration: 'none',
                                        borderRadius: 1,
                                        '&:hover': { opacity: 0.85 },
                                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={HEADER_LOGO_SRC}
                                        alt=""
                                        sx={{
                                            height: isMobile ? 26 : 28,
                                            width: isMobile ? 26 : 28,
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: isMobile ? "flex-start" : "stretch",
                                        gap: centeredSubtitle ? 0.25 : 0,
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        component="div"
                                        sx={{
                                            fontWeight: 600,
                                            color: titleColor,
                                            textAlign: isMobile ? "left" : "center",
                                            lineHeight: isMobile ? 1.3 : 1.25,
                                            fontSize: centeredSubtitle
                                                ? isMobile
                                                    ? "1.2rem"
                                                    : "1.35rem"
                                                : isMobile
                                                  ? "1.1rem"
                                                  : "1.25rem",
                                            width: "100%",
                                            overflow: "hidden",
                                            display: "-webkit-box",
                                            WebkitBoxOrient: "vertical",
                                            WebkitLineClamp: 2,
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {centeredTitle}
                                    </Typography>
                                    {centeredSubtitle ? (
                                        <Typography
                                            variant="caption"
                                            component="div"
                                            color="text.secondary"
                                            sx={{
                                                width: "100%",
                                                textAlign: isMobile ? "left" : "center",
                                                lineHeight: 1.25,
                                                fontWeight: 400,
                                                overflow: "hidden",
                                                display: "-webkit-box",
                                                WebkitBoxOrient: "vertical",
                                                WebkitLineClamp: 2,
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {centeredSubtitle}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>

                        {/* Right side icons */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 1,
                            ml: 'auto',
                            position: 'relative',
                            zIndex: 1002,
                            flexShrink: 0
                        }}>
                            {headerExtra}
                            {renderDashboardChip()}
                            {renderSignInControl()}
                            
                            {isCreator && (
                                <Tooltip title={isOnCreatorPage ? "Go to home page" : "Go to the creator page"}>
                                    <IconButton
                                        color="primary"
                                        onClick={isOnCreatorPage ? onHomeClick : onCreatorClick}
                                        sx={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                                    >
                                        {isOnCreatorPage ? <HomeIcon /> : <AutoFixHighIcon />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            {isAdmin && (
                                <Tooltip title="Go to the admin page">
                                    <IconButton
                                        color="secondary"
                                        onClick={onAdminClick}
                                        sx={{ minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                                    >
                                        <AdminIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Toolbar>
                </AppBar>
            ) : (
                // Full header for other pages
                <AppBar
                    position="static"
                    data-site-chrome="header"
                    sx={{
                        ...headerTone,
                        minHeight: 64,
                        zIndex: 1100,
                        position: 'relative',
                        pt: appBarSafeAreaPt,
                    }}
                >
                    <Toolbar sx={{ minHeight: '64px !important' }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={onMenuClick}
                            sx={{ mr: 2, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                component={RouterLink}
                                to="/"
                                aria-label="Home"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    textDecoration: 'none',
                                    borderRadius: 1,
                                    '&:hover': { opacity: 0.85 },
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
                                }}
                            >
                                <Box
                                    component="img"
                                    src={HEADER_LOGO_SRC}
                                    alt=""
                                    sx={{
                                        height: 36,
                                        width: 36,
                                        objectFit: 'contain',
                                        display: 'block'
                                    }}
                                />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                        fontWeight: 600,
                                        color: titleColor
                                    }}
                                >
                                    {title}
                                </Typography>
                            </Box>
                        </Box>

                        {headerExtra}
                        {renderDashboardChip()}
                        {renderSignInControl({ ml: 1 })}
                        
                        {isCreator && (
                            <Tooltip title={isOnCreatorPage ? "Go to home page" : "Go to the creator page"}>
                                <IconButton
                                    color="primary"
                                    onClick={isOnCreatorPage ? onHomeClick : onCreatorClick}
                                    sx={{ ml: 1, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                                >
                                    {isOnCreatorPage ? <HomeIcon /> : <AutoFixHighIcon />}
                                </IconButton>
                            </Tooltip>
                        )}
                        
                        {isAdmin && (
                            <Tooltip title="Go to the admin page">
                                <IconButton
                                    color="secondary"
                                    onClick={onAdminClick}
                                    sx={{ ml: 1, minWidth: 44, minHeight: 44, touchAction: 'manipulation' }}
                                >
                                    <AdminIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Toolbar>
                </AppBar>
            )}
        </>
    );
};

export default MinimalHeader;
